/** Inventory posting tab — purchase order numbering + confirmation defaults. */
import { useCallback, useEffect, useState } from "react";
import {
  getManufacturingPurchasingSettings,
  upsertManufacturingPurchasingSettings,
} from "@/lib/manufacturing/purchaseOrderClient";
import {
  DEFAULT_PURCHASING_SETTINGS,
  type MfgPurchasingSettings,
} from "@/lib/manufacturing/purchaseOrderTypes";
import { CcEmailChipInput } from "@/components/manufacturing/CcEmailChipInput";
import {
  ghostBtnClass,
  inputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { ccEmailListsEqual } from "@/lib/manufacturing/ccEmails";

export function PurchaseOrderSettingsBody(props: { enabled: boolean }) {
  const [settings, setSettings] = useState<MfgPurchasingSettings>(DEFAULT_PURCHASING_SETTINGS);
  const [original, setOriginal] = useState<MfgPurchasingSettings>(DEFAULT_PURCHASING_SETTINGS);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);

  const load = useCallback(async () => {
    setBusy(true);
    setMsg(null);
    const res = await getManufacturingPurchasingSettings();
    setBusy(false);
    if (!res.ok) {
      setMsg({ text: res.error, ok: false });
      return;
    }
    setSettings(res.data.settings);
    setOriginal(res.data.settings);
  }, []);

  useEffect(() => {
    if (props.enabled) void load();
  }, [load, props.enabled]);

  const dirty =
    settings.next_po_number !== original.next_po_number ||
    settings.po_number_prefix !== original.po_number_prefix ||
    settings.default_confirmation_message !== original.default_confirmation_message ||
    !ccEmailListsEqual(settings.default_po_cc_emails ?? [], original.default_po_cc_emails ?? []) ||
    !ccEmailListsEqual(
      settings.default_invoice_cc_emails ?? [],
      original.default_invoice_cc_emails ?? []
    );

  async function save() {
    setBusy(true);
    setMsg(null);
    const res = await upsertManufacturingPurchasingSettings(settings);
    setBusy(false);
    if (!res.ok) {
      setMsg({ text: res.error, ok: false });
      return;
    }
    setSettings(res.data.settings);
    setOriginal(res.data.settings);
    setMsg({ text: "Purchase order settings saved.", ok: true });
  }

  return (
    <div className="mt-3 space-y-3">
      {msg ? (
        <div
          className={
            msg.ok
              ? "rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-100"
              : "rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-900/30 dark:text-rose-100"
          }
        >
          {msg.text}
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-xs">
          <span className="font-semibold">PO number prefix</span>
          <input
            className={`${inputClass} mt-1 w-full`}
            value={settings.po_number_prefix}
            disabled={!props.enabled || busy}
            onChange={(e) =>
              setSettings((s) => ({ ...s, po_number_prefix: e.target.value }))
            }
            placeholder="e.g. PO-"
          />
        </label>
        <label className="text-xs">
          <span className="font-semibold">Next PO number</span>
          <input
            type="number"
            min={1}
            step={1}
            className={`${inputClass} mt-1 w-full`}
            value={settings.next_po_number}
            disabled={!props.enabled || busy}
            onChange={(e) =>
              setSettings((s) => ({
                ...s,
                next_po_number: Math.max(1, Math.floor(Number(e.target.value) || 1)),
              }))
            }
          />
        </label>
      </div>

      <label className="block text-xs">
        <span className="font-semibold">Default confirmation message</span>
        <textarea
          className={`${inputClass} mt-1 w-full`}
          rows={4}
          value={settings.default_confirmation_message}
          disabled={!props.enabled || busy}
          onChange={(e) =>
            setSettings((s) => ({ ...s, default_confirmation_message: e.target.value }))
          }
        />
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-xs">
          <span className="font-semibold">Default PO CC emails</span>
          <CcEmailChipInput
            emails={settings.default_po_cc_emails ?? []}
            onChange={(emails) => setSettings((s) => ({ ...s, default_po_cc_emails: emails }))}
            disabled={!props.enabled || busy}
            placeholder="Type an email, then Enter or comma"
          />
          <span className="mt-0.5 block text-[11px] font-normal text-gray-500">
            Copied onto new purchase orders and printed as a comma-delimited list.
          </span>
        </label>
        <label className="block text-xs">
          <span className="font-semibold">Default invoice CC emails</span>
          <CcEmailChipInput
            emails={settings.default_invoice_cc_emails ?? []}
            onChange={(emails) => setSettings((s) => ({ ...s, default_invoice_cc_emails: emails }))}
            disabled={!props.enabled || busy}
            placeholder="Type an email, then Enter or comma"
          />
          <span className="mt-0.5 block text-[11px] font-normal text-gray-500">
            Copied onto vendor invoices on each purchase order.
          </span>
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          disabled={!props.enabled || busy || !dirty}
          onClick={() => void save()}
        >
          {busy ? "Saving…" : "Save settings"}
        </button>
        <button
          type="button"
          className={ghostBtnClass}
          disabled={busy || !dirty}
          onClick={() => setSettings(original)}
        >
          Reset
        </button>
      </div>
    </div>
  );
}
